// SVG text has no `text-overflow`, so a name that will not fit the gutter has
// to be cut to a width that is MEASURED rather than guessed at. A canvas
// context measures in the same font the chart draws in, which is the only way
// a serif stack's real advances are known.
//
// The font is read off a live element once, so it follows the stylesheet
// rather than repeating it here.
const widths = new Map();

let context;
let stack;

function measurer() {
  if (context !== undefined) return context;

  // jsdom has no 2d context and logs a "not implemented" notice for merely
  // asking, so it is not asked. Nothing there reports a real width anyway: the
  // estimate below is what the component suite sees, and the browser suite is
  // where truncation is actually asserted.
  const headless = typeof navigator !== "undefined" && /jsdom/i.test(navigator.userAgent);
  const canvas = typeof document === "undefined" || headless
    ? null
    : document.createElement("canvas");
  context = canvas?.getContext?.("2d") || null;
  // The chart inherits the page's serif stack; measuring in a generic "serif"
  // measures a different face with different advances.
  stack = context ? getComputedStyle(document.body).fontFamily : "serif";
  return context;
}

// ~0.5em per character is a fair average for a serif face, and it is only ever
// reached where nothing can measure.
function estimate(text, size) {
  return text.length * size * 0.5;
}

export function textWidth(text, { size, weight = 400 } = {}) {
  const key = `${weight}|${size}|${text}`;
  const known = widths.get(key);
  if (known !== undefined) return known;

  const ctx = measurer();
  let width;
  if (ctx) {
    ctx.font = `${weight} ${size}px ${stack}`;
    width = ctx.measureText(text).width;
  } else {
    width = estimate(text, size);
  }

  widths.set(key, width);
  return width;
}

// The longest prefix that fits, with an ellipsis standing for what was cut.
// The full name is still on the row's accessible name and in its tooltip, so
// nothing is only available in the truncated form.
export function fitText(text, maxWidth, options) {
  if (!text) return text;
  if (maxWidth <= 0) return "";
  if (textWidth(text, options) <= maxWidth) return text;

  let low = 0;
  let high = text.length;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (textWidth(`${text.slice(0, middle).trimEnd()}…`, options) <= maxWidth) low = middle;
    else high = middle - 1;
  }

  return low === 0 ? "…" : `${text.slice(0, low).trimEnd()}…`;
}

export function resetTextMeasurement() {
  widths.clear();
  context = undefined;
  stack = undefined;
}
